import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useActionRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { EmptyInfo } from "../../components/Empty";
import { GetCompliantPageWrapper } from "../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../components/layout/header/Header";
import { Para } from "../../components/typography/Para";
import { Paras } from "../../components/typography/Paras";
import {
  useOrganizationVendors,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
} from "../../hooks";
import { useAuthorizations } from "../../hooks/useAuthorizations";
import { useTasks } from "../../hooks/useTasks";
import { isGoogleAnalytics } from "../../util/vendors";
import { OrganizationGoogleAnalyticsForm } from "../organization/OrganizationGoogleAnalyticsForm";
import { GetCompliantFooter } from "./ProgressFooter";
import { SURVEY_GATES_SURVEY_NAME } from "../../components/organisms/survey/SurveyScanEffect";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import { UpdateOrganizationGoogleAnalyticsWebPropertyDto } from "../../../common/service/server/controller/OrganizationController";
import { UpdatePCGoogleAnalyticsMeasurementIdDto } from "../../../common/service/server/controller/PrivacyCenterController";
import QueryString from "qs";
import { useSnackBar } from "@truevault/aquilo";

const GA_TASK_SURVEY_SLUG = "ga-task-created";

export const IntegrationsPage: React.FC = () => {
  const orgId = usePrimaryOrganizationId();
  const [org, orgRequest, refreshOrg] = usePrimaryOrganization();
  const [dataRecipients, dataRecipientsRequest] = useOrganizationVendors(orgId, undefined, true);

  const enqueueSnackbar = useSnackBar();

  // Handle OAuth redirect parameters
  const query = QueryString.parse(location.search.replace(/^\?/, ""));
  const {
    appIntegration,
    error: gaError,
    error_message: gaErrorMessage,
    error_description: gaErrorDescription,
  } = query;

  // Future: The GA Measurement IDs are site (privacy center)-specific. Eventually we'll support orgs with multiple live
  // PCs and will need to refactor this (and many other places) to account for that.
  const [privacyCenter, privacyCentersRequest] = usePrivacyCenter(orgId);

  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(false);

  const { answers, setAnswer, request: surveyRequest } = useSurvey(orgId, SURVEY_GATES_SURVEY_NAME);
  const showTaskMessage = answers[GA_TASK_SURVEY_SLUG] == "true";

  const [orgGASettings, setOrgGASettings] =
    useState<UpdateOrganizationGoogleAnalyticsWebPropertyDto>(() => ({
      webProperty: org.gaWebProperty,
    }));
  const [pcGASettings, setPCGASettings] = useState<UpdatePCGoogleAnalyticsMeasurementIdDto>(() => ({
    gaMeasurementId: privacyCenter?.gaMeasurementId,
  }));

  const updateOrganizationGAApi = useCallback(
    (submitValues) => Api.organization.updateGaWebProperty(orgId, submitValues),
    [orgId],
  );
  const updatePCGAApi = useCallback(
    (submitValues) =>
      Api.privacyCenter.updateGaMeasurementId(orgId, privacyCenter?.id, submitValues),
    [orgId, privacyCenter, pcGASettings],
  );

  const { fetch: updateOrgGa, request: updateOrgGaRequest } = useActionRequest({
    api: async () => {
      await updateOrganizationGAApi(orgGASettings);
      await refreshOrg();
    },
  });
  const { fetch: updatePCGA, request: updatePCGARequest } = useActionRequest({
    api: async () => {
      await updatePCGAApi(pcGASettings);
      await refreshOrg();
    },
  });

  // These debounces aren't strictly necessary since they're just updating the local state. They seem inspired by the ones
  // in the Get Compliant version of this page where we update the database onBlur. Here we wait until the user clicks Save.
  const debouncedSetOrgGASettings = _.debounce(setOrgGASettings, 300);
  const debouncedSetPCGASettings = _.debounce(setPCGASettings, 300);

  const googleAnalytics = useMemo(
    () => dataRecipients?.find((dr) => isGoogleAnalytics(dr)),
    [dataRecipients],
  );

  const { authorizations, request: authorizationsRequest } = useAuthorizations(orgId);

  const [tasks, tasksRequest, refreshTasks] = useTasks(orgId);

  const {
    fetch: addAuthorizeGoogleAnalyticsTask,
    request: addAuthorizeGoogleAnalyticsTaskRequest,
  } = useActionRequest({
    api: () => Api.tasks.createAuthorizeGoogleAnalytics(orgId),
    onSuccess: () => {
      refreshTasks();
      setAnswer(GA_TASK_SURVEY_SLUG, "true");
    },
  });

  const hasAuthorizeGoogleAnalyticsTask = useMemo(
    () => tasks?.some((t) => t.taskKey === "AUTHORIZE_GOOGLE_ANALYTICS") ?? false,
    [tasks],
  );

  const nextDisabled =
    (!authorizations?.hasGoogle && !hasAuthorizeGoogleAnalyticsTask) || hasValidationErrors;

  // Handle Google Analytics OAuth errors
  useEffect(() => {
    if (appIntegration === "googleAnalytics" && (gaError || gaErrorMessage || gaErrorDescription)) {
      // Support both backend format (error_message) and Google OAuth standard (error_description)
      const errorMsg =
        gaErrorMessage ||
        gaErrorDescription ||
        gaError ||
        "An error occurred during Google Analytics authorization";
      enqueueSnackbar(`Failed to connect Google Analytics: ${errorMsg}`, { variant: "error" });
      // Clear the error parameters from URL
      history.replaceState(null, "", window.location.href.split("?")[0]);
    }
  }, [appIntegration, gaError, gaErrorMessage, gaErrorDescription, enqueueSnackbar]);

  return (
    <GetCompliantPageWrapper
      requests={[
        orgRequest,
        dataRecipientsRequest,
        authorizationsRequest,
        tasksRequest,
        surveyRequest,
        privacyCentersRequest,
      ]}
      progressFooter={
        <GetCompliantFooter
          saveRequests={[
            addAuthorizeGoogleAnalyticsTaskRequest,
            updateOrgGaRequest,
            updatePCGARequest,
          ]}
          presentStep="DataRetention.VendorIntegrations"
          nextDisabled={nextDisabled}
          nextLabel="Next"
          nextOnClick={async () => {
            await updateOrgGa();
            await updatePCGA();
          }}
        />
      }
    >
      <PageHeader
        titleContent="API Integrations"
        descriptionContent={
          <Paras>
            <Para>
              Connect your accounts for the following services to enable our integrations with these
              services. These integrations facilitate functionality like automatic deletion of
              consumer data and respect for GPC signals.
            </Para>
          </Paras>
        }
      />

      {googleAnalytics ? (
        <OrganizationGoogleAnalyticsForm
          showDoItLater
          organization={org}
          privacyCenter={privacyCenter}
          loading={Boolean(updateOrgGaRequest.running || updatePCGARequest.running)}
          onUpdateGAPropertyId={debouncedSetOrgGASettings}
          onUpdateGAMeasurementId={debouncedSetPCGASettings}
          onErrorStateUpdate={(hasErrors) => setHasValidationErrors(hasErrors)}
          googleAnalytics={googleAnalytics}
          saveOnAuthorizeGaWebProperty={() => {
            updateOrgGa();
            updatePCGA();
          }}
          hasAuthorizeGoogleAnalyticsTask={hasAuthorizeGoogleAnalyticsTask && showTaskMessage}
          addAuthorizeGoogleAnalyticsTask={addAuthorizeGoogleAnalyticsTask}
          addAuthorizeGoogleAnalyticsTaskRequest={addAuthorizeGoogleAnalyticsTaskRequest}
        />
      ) : (
        <EmptyInfo>There are no integrations to configure</EmptyInfo>
      )}
    </GetCompliantPageWrapper>
  );
};
